import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, UserPlus, Briefcase, Copy, Check, ExternalLink,
  ChevronDown, ChevronUp, MoreVertical, Building2, Shield, Trash2, RotateCcw,
  Calendar, FileText, Phone, MapPin, Search, X, Upload, Loader2, RefreshCw, Link2, MessageCircle, Edit2, AlertTriangle, Coins, Clock, Lock,
  LayoutList, LayoutGrid,
} from 'lucide-react';

// shadcn/ui
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Services & types
import { services as allAvailableServices } from '@/data/services';
import {
  createEmployee,
  updateEmployeeFull,
  getAvailableEmployees,
  updateEmployeeStatus,
  deleteEmployee,
  getDeletedEmployees,
  restoreEmployee,
  permanentlyDeleteEmployee,
  permanentlyDeleteAllDeletedEmployees,
  getEmployeeDocuments,
} from '../../services/employeeService';
import {
  assignWorkerToClient,
  deactivateIDCardLink,
  sendIDCardLinkToClient,
  buildShareableUrl,
} from '../../services/assignmentService';
import { supabase } from '../../lib/supabase';
import { EmployeeIDCard } from '../hr/EmployeeIDCard';
import PayslipGenerator from './PayslipGenerator';
import type { Employee, EmployeeStatus, CreateEmployeeInput } from '../../types/hr';
import { formatIdCardDuty } from '../../utils/employeeIdCard';
import { useAuth } from '../../contexts/AuthContext';
import { maskPhoneNumber } from '../../utils/phoneMask';
import RequestDeletionModal from '../common/RequestDeletionModal';

// ── Types ─────────────────────────────────────────────────

interface ActiveAssignment {
  id: string;
  employee_id: string;
  client_id: string;
  assigned_at: string;
  assignment_status: string;
  notes: string | null;
  employee: Employee;
  client: { id: string; client_name: string; phone_number: string | null };
  shareableUrl: string | null;
  token: string | null;
}

interface Client {
  id: string;
  client_name: string;
  company_name: string | null;
  phone_number: string | null;
}

// ── Helpers ───────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function statusBadge(status: EmployeeStatus) {
  const map: Record<EmployeeStatus, { label: string; className: string }> = {
    available: { label: 'Available', className: 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-sm shadow-emerald-500/5' },
    assigned:  { label: 'Assigned',  className: 'bg-blue-50 text-blue-600 border-blue-100 shadow-sm shadow-blue-500/5' },
    inactive:  { label: 'Inactive',  className: 'bg-slate-50 text-slate-500 border-slate-200 shadow-sm shadow-slate-500/5' },
  };
  const { label, className } = map[status] ?? map.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'available' ? 'bg-emerald-400' : status === 'assigned' ? 'bg-blue-400' : 'bg-slate-400'}`} />
      {label}
    </span>
  );
}

function getPaymentScheme(employee: Employee) {
  return {
    label: 'Daily Rate',
    rateLabel: 'Daily Rate',
    amount: employee.rate_10hr ?? 0,
    duty: 'Daily Rate',
  };
}

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="ml-1 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
      title="Copy link"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Loading Skeletons ─────────────────────────────────────

function WorkerCardSkeleton() {
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl p-3 sm:p-3.5 flex flex-col gap-2.5 justify-between">
      <div className="flex items-center gap-2.5">
        <Skeleton className="w-10 h-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-1.5 min-w-0">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-8 flex-1 rounded-lg" />
        <Skeleton className="h-8 flex-1 rounded-lg" />
      </div>
    </div>
  );
}

function TableRowSkeleton({ cols = 6 }: { cols?: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full max-w-[140px]" />
        </td>
      ))}
    </tr>
  );
}

// ── Add Employee Dialog ───────────────────────────────────

interface AddEmployeeDialogProps {
  employee?: Employee | null;
  open: boolean;
  onClose: () => void;
  onCreated: (emp: Employee, autoPreview?: boolean) => void;
}

function AddEmployeeDialog({ employee, open, onClose, onCreated }: AddEmployeeDialogProps) {
  const [form, setForm] = useState<CreateEmployeeInput>({ 
    full_name: '', job_title: '', 
    services: [],
    phone: '', aadhaar_number: '', address: '', dob: '',
    rate_10hr: 0, rate_24hr: 800,
    username: '', password: '', documents: []
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (employee) {
      setForm({
        full_name: employee.full_name || '',
        job_title: employee.job_title || '',
        services: employee.services || [],
        phone: employee.phone || '',
        aadhaar_number: employee.aadhaar_number || '',
        address: employee.address || '',
        dob: employee.dob || '',
        rate_10hr: employee.rate_10hr || 0,
        rate_24hr: employee.rate_24hr || 800,
        username: '', password: '', documents: [],
      });
      (setForm as any)((f: any) => ({...f, gender: employee.gender || '', experience: employee.experience || ''}));
      setPhotoPreview(employee.photo_url || null);
    } else {
      setForm({
        full_name: '', job_title: '',
        services: [],
        phone: '', aadhaar_number: '', address: '', dob: '',
        rate_10hr: 0, rate_24hr: 800,
        username: '', password: '', documents: []
      });
      (setForm as any)((f: any) => ({...f, gender: '', experience: ''}));
      setPhotoPreview(null);
    }
  }, [employee]);


  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB.'); return; }
    setForm(f => ({ ...f, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setForm((f: any) => ({ ...f, documents: [...(f.documents || []), ...files] }));
  };

  const removeDocument = (index: number) => {
    setForm((f: any) => {
      const newDocs = [...(f.documents || [])];
      newDocs.splice(index, 1);
      return { ...f, documents: newDocs };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error('Full name is required.'); return; }
    if (!form.job_title.trim()) { toast.error('Job title is required.'); return; }
    setIsSubmitting(true);
    try {
      
      let emp: Employee;
      if (employee) {
        emp = await updateEmployeeFull({ id: employee.id, ...form });
        toast.success(`Employee updated successfully! ✅`);
      } else {
        emp = await createEmployee(form);
        toast.success(`Employee created! ID: ${emp.employee_id} ✅`);
      }
      onCreated(emp, !employee); // Trigger auto-preview only on create
      onClose();

      // Reset form but keep the newly created emp for preview
      setForm({ 
        full_name: '', job_title: '',
        services: [],
        phone: '', aadhaar_number: '', address: '', dob: '',
        rate_10hr: 0, rate_24hr: 800,
        username: '', password: '', documents: []
      });
      setPhotoPreview(null);
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to create employee.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {employee ? <Edit2 className="w-5 h-5 text-primary" /> : <UserPlus className="w-5 h-5 text-primary" />}
            {employee ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Photo upload */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-5 flex flex-col items-center gap-2 cursor-pointer transition-colors
              ${isDragging ? 'border-primary/40 bg-primary/5' : 'border-slate-200 hover:border-primary/30 hover:bg-slate-50'}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover ring-4 ring-primary/10" />
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setPhotoPreview(null); setForm(f => ({ ...f, photo: undefined })); }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-slate-300" />
                <p className="text-sm text-slate-500">Drag & drop or <span className="text-primary font-medium">click to upload</span></p>
                <p className="text-xs text-slate-400">JPEG, PNG, WebP · max 5 MB</p>
              </>
            )}
            <input ref={fileInputRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Full Name <span className="text-red-400">*</span></label>
              <Input className="mt-1" placeholder="e.g. Anita Sharma" value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Job Title <span className="text-red-400">*</span></label>
                <Input className="mt-1" placeholder="e.g. Registered Nurse" value={form.job_title}
                  onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Gender</label>
                <select 
                  className="w-full mt-1 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={(form as any).gender ?? ''}
                  onChange={e => {
                    const gender = e.target.value;
                    // Auto-fill rates based on gender
                    const rate10 = gender === 'Female' ? 500 : gender === 'Male' ? 600 : (form as any).rate_10hr ?? 0;
                    const rate24 = 800;
                    setForm(f => ({ ...f, gender, rate_10hr: rate10, rate_24hr: rate24 } as any));
                  }}
                >
                  <option value="" disabled>Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Services & Skills</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[...allAvailableServices.map(s => s.title), 'All Services'].map(service => {
                  const active = form.services?.includes(service);
                  return (
                    <button type="button" key={service}
                      onClick={() => setForm(f => ({
                        ...f,
                        services: active 
                          ? f.services!.filter(s => s !== service)
                          : service === 'All Services' ? ['All Services'] : [...(f.services?.filter(s=>s !== 'All Services')||[]), service]
                      }))}
                      className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                        active ? 'bg-primary/10 text-primary border-primary/20' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Phone</label>
              <Input className="mt-1" placeholder="98765 43210" value={form.phone ?? ''}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Aadhaar</label>
                <Input className="mt-1" placeholder="0000 0000 0000" value={form.aadhaar_number ?? ''}
                  onChange={e => setForm(f => ({ ...f, aadhaar_number: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date of Birth</label>
                <Input className="mt-1" type="date" value={form.dob ?? ''}
                  onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} />
              </div>
            </div>


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Residential Address</label>
                <Input className="mt-1" placeholder="Full Address" value={form.address ?? ''}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Experience</label>
                <Input className="mt-1" placeholder="e.g. 5 Years" value={(form as any).experience ?? ''}
                  onChange={e => setForm(f => ({ ...f, experience: e.target.value } as any))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">ID Proofs & Documents</label>
              <div 
                className="border border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => docInputRef.current?.click()}
              >
                <Upload className="w-5 h-5 text-slate-400 mb-2" />
                <p className="text-xs text-slate-500 text-center">Click to upload Aadhaar, PAN, or other proofs</p>
                <input ref={docInputRef} type="file" className="hidden" multiple accept="image/*,.pdf" onChange={handleDocumentChange} />
              </div>
              
              {form.documents && form.documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {form.documents.map((doc, idx) => (
                    <div key={idx} className="flex flex-row items-center justify-between p-2 bg-slate-50 border border-slate-100 rounded-md text-xs">
                      <span className="truncate flex-1 font-medium text-slate-700">{doc.name}</span>
                      <button type="button" onClick={() => removeDocument(idx)} className="ml-2 text-red-500 hover:text-red-700 p-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-2 border-b border-slate-100">
               <div>
                <label className="text-sm font-medium text-slate-700 text-xs uppercase tracking-wider">10-Hour Shift Rate (₹/day)</label>
                <Input type="number" className="mt-1" 
                  value={(form as any).rate_10hr ?? 0}
                  onChange={e => setForm(f => ({ ...f, rate_10hr: Number(e.target.value) } as any))}
                  placeholder="Male ₹600 / Female ₹500"
                />
              </div>
               <div>
                <label className="text-sm font-medium text-slate-700 text-xs uppercase tracking-wider">24-Hour Shift Rate (₹/day)</label>
                <Input type="number" className="mt-1"
                  value={(form as any).rate_24hr ?? 800}
                  onChange={e => setForm(f => ({ ...f, rate_24hr: Number(e.target.value) } as any))}
                  placeholder="₹800"
                />
              </div>
              <p className="col-span-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                💡 Rate is auto-selected based on the client’s service hours: ≤10h → 10-hr rate, &gt;10h → 24-hr rate.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90 text-white gap-2" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {isSubmitting ? (employee ? 'Saving...' : 'Creating...') : (employee ? 'Save Changes' : 'Create Employee')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Assign to Client Dialog ───────────────────────────────

interface AssignDialogProps {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

function AssignDialog({ employee, open, onClose, onAssigned }: AssignDialogProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isEndDateOpenEnded, setIsEndDateOpenEnded] = useState(true);
  const [hoursPerDay, setHoursPerDay] = useState<number>(10);
  const [minStartDate, setMinStartDate] = useState<string>('');
  const [lockedDateWarning, setLockedDateWarning] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ url: string; whatsappSent: boolean; whatsappError?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // New client inline form
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  const debouncedSearch = useDebounce(clientSearch, 250);

  useEffect(() => {
    if (!open) {
      setResult(null); setSelectedClient(null); setNotes(''); setClientSearch('');
      setShowNewClient(false); setStartDate(''); setEndDate(''); setIsEndDateOpenEnded(true); setHoursPerDay(10);
      setMinStartDate(''); setLockedDateWarning('');
    }
  }, [open]);

  useEffect(() => {
    // Search crm_leads: include all active, monthly billing, and in-progress leads (exclude only archived/lost)
    let query = supabase.from('crm_leads')
      .select('id, name, phone, whatsapp_number, pipeline_stage')
      .is('deleted_at', null)
      .not('pipeline_stage', 'in', '("Archived","Lost","Closed Lost")');

    if (debouncedSearch && debouncedSearch.trim().length > 0) {
      query = query.ilike('name', `%${debouncedSearch.trim()}%`);
    }

    query
      .order('name', { ascending: true })
      .limit(30)
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching clients for assignment:', error);
          return;
        }
        const formatted = (data ?? []).map(l => ({
          id: l.id,
          client_name: l.name,
          company_name: l.pipeline_stage,
          phone_number: l.whatsapp_number || l.phone
        }));
        setClients(formatted);
      });
  }, [debouncedSearch]);

  // Fetch and lock shift requirements based on selected client's active service or quotation
  useEffect(() => {
    if (!selectedClient) return;

    async function fetchClientShift() {
      try {
        // 1. Check services table first
        const { data: svc } = await supabase
          .from('services')
          .select('hours_per_day, start_date, end_date')
          .eq('client_id', selectedClient!.id)
          .in('status', ['active', 'pending'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // 2. Check crm_quotations
        const { data: quote } = await supabase
          .from('crm_quotations')
          .select('hours_per_day, shift_type, start_date, end_date')
          .eq('lead_id', selectedClient!.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // 3. Fallback to crm_leads notes & consent
        const { data: lead } = await supabase
          .from('crm_leads')
          .select('notes, client_consents(offered_time)')
          .eq('id', selectedClient!.id)
          .maybeSingle();

        const svcHours = Number(svc?.hours_per_day || 0);
        const qHours = String(quote?.hours_per_day || quote?.shift_type || '');
        const notesStr = String(lead?.notes || '');
        const offeredTime = String(lead?.client_consents?.[0]?.offered_time || '');

        if (svcHours > 0) {
          setHoursPerDay(svcHours >= 12 ? 24 : 10);
        } else if (
          qHours.includes('24') || 
          qHours.toLowerCase().includes('live') || 
          notesStr.includes('24') || 
          offeredTime.includes('24')
        ) {
          setHoursPerDay(24);
        } else {
          setHoursPerDay(10);
        }

        // 4. Check if THIS worker was previously assigned and released/completed for this client
        let latestLockedDate: string | null = null;
        if (employee?.id && selectedClient?.id) {
          const { data: pastAssignments } = await supabase
            .from('worker_assignments')
            .select('id, end_date, start_date')
            .eq('employee_id', employee.id)
            .eq('client_id', selectedClient.id)
            .in('assignment_status', ['completed', 'relieved', 'ended']);

          (pastAssignments || []).forEach(p => {
            const ed = p.end_date ? p.end_date.split('T')[0] : null;
            if (ed && (!latestLockedDate || ed > latestLockedDate)) {
              latestLockedDate = ed;
            }
          });

          const pastIds = (pastAssignments || []).map(p => p.id);
          if (pastIds.length > 0) {
            const { data: attLogs } = await supabase
              .from('attendance')
              .select('duty_date')
              .eq('worker_id', employee.id)
              .in('assignment_id', pastIds)
              .order('duty_date', { ascending: false })
              .limit(1);
            if (attLogs?.[0]?.duty_date && (!latestLockedDate || attLogs[0].duty_date > latestLockedDate)) {
              latestLockedDate = attLogs[0].duty_date;
            }
          }

          // Check locked final payroll records
          const { data: finalPayrolls } = await supabase
            .from('payroll')
            .select('period_end')
            .eq('worker_id', employee.id)
            .eq('type', 'final')
            .order('period_end', { ascending: false })
            .limit(1);
          if (finalPayrolls?.[0]?.period_end && (!latestLockedDate || finalPayrolls[0].period_end > latestLockedDate)) {
            latestLockedDate = finalPayrolls[0].period_end;
          }
        }

        let calculatedStartDate = '';
        if (svc?.start_date) calculatedStartDate = svc.start_date;
        else if (quote?.start_date) calculatedStartDate = quote.start_date;
        else calculatedStartDate = new Date().toISOString().slice(0, 10);

        if (latestLockedDate) {
          const d = new Date(latestLockedDate + 'T00:00:00');
          d.setDate(d.getDate() + 1);
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const nextAllowed = `${yyyy}-${mm}-${dd}`;
          setMinStartDate(nextAllowed);
          setLockedDateWarning(`Worker was previously relieved up to ${latestLockedDate}. Next assignment can only start from ${nextAllowed}.`);
          if (!calculatedStartDate || calculatedStartDate <= latestLockedDate) {
            calculatedStartDate = nextAllowed;
          }
        } else {
          setMinStartDate('');
          setLockedDateWarning('');
        }

        setStartDate(calculatedStartDate);

        if (svc?.end_date) {
          setEndDate(svc.end_date);
          setIsEndDateOpenEnded(false);
        } else if (quote?.end_date) {
          setEndDate(quote.end_date);
          setIsEndDateOpenEnded(false);
        } else {
          setEndDate('');
          setIsEndDateOpenEnded(true);
        }
      } catch (err) {
        console.warn('Could not fetch client shift details:', err);
      }
    }

    fetchClientShift();
  }, [selectedClient, employee]);

  const handleCreateClient = async () => {
    if (!newClientName.trim()) { toast.error('Client name required.'); return; }
    setIsCreatingClient(true);
    try {
      const { data, error } = await supabase.from('clients')
        .insert({ client_name: newClientName.trim(), phone_number: newClientPhone.trim() || null })
        .select().single();
      if (error) throw error;
      setSelectedClient(data);
      setShowNewClient(false);
      setNewClientName('');
      setNewClientPhone('');
      toast.success('Client added!');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsCreatingClient(false);
    }
  };

  const handleAssign = async () => {
    if (!employee || !selectedClient) { toast.error('Please select a client.'); return; }
    if (!startDate) { toast.error('Please set an assignment start date.'); return; }
    if (minStartDate && startDate < minStartDate) {
      toast.error(`Cannot assign before ${minStartDate}. This worker was already released/marked on prior duties.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await assignWorkerToClient(
        employee.id,
        selectedClient.id,
        notes,
        0, // Deposit handled at Client/Service level
        false,
        {
          startDate,
          endDate: endDate || undefined,
          serviceType: 'date_range',
          hoursPerDay: hoursPerDay || 10,
          totalBillAmount: 0,
        }
      );

      // Save the start and end dates to worker_assignments
      if (res.assignment?.id) {
        await supabase.from('worker_assignments').update({
          start_date: startDate,
          end_date: endDate || null,
          hours_per_day: hoursPerDay || 10,
        }).eq('id', res.assignment.id);
      }

      // Also ensure linking to active service in services table
      try {
        const { data: svcs } = await supabase
          .from('services')
          .select('id, status')
          .eq('client_id', selectedClient.id)
          .in('status', ['active', 'pending'])
          .limit(1);

        let targetServiceId = svcs?.[0]?.id;
        if (!targetServiceId) {
          const { data: quote } = await supabase
            .from('crm_quotations')
            .select('complete_month_rate, incomplete_month_rate, deposit')
            .eq('lead_id', selectedClient.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const { data: lead } = await supabase
            .from('crm_leads')
            .select('complete_month_daily_rate, incomplete_month_daily_rate, assigned_worker_role, notes')
            .eq('id', selectedClient.id)
            .maybeSingle();

          let resolvedServiceName = lead?.assigned_worker_role;
          if (!resolvedServiceName && lead?.notes) {
            const match = lead.notes.match(/Service:\s*([^\n\r]+)/i);
            if (match && match[1]) resolvedServiceName = match[1].trim();
          }
          if (!resolvedServiceName) resolvedServiceName = 'Home Care Service';

          const cmRate = quote?.complete_month_rate || lead?.complete_month_daily_rate || (hoursPerDay === 24 ? 1000 : 500);
          const imRate = quote?.incomplete_month_rate || lead?.incomplete_month_daily_rate || (hoursPerDay === 24 ? 2000 : 1000);
          const depAmount = quote?.deposit || 0;

          const { data: newSvc } = await supabase
            .from('services')
            .insert({
              client_id: selectedClient.id,
              lead_id: selectedClient.id,
              service_type: resolvedServiceName,
              hours_per_day: hoursPerDay,
              start_date: startDate,
              end_date: endDate || null,
              status: 'active',
              deposit_amount: depAmount,
              deposit_status: 'pending',
              complete_month_daily_rate: cmRate,
              incomplete_month_daily_rate: imRate,
            })
            .select('id')
            .single();
          targetServiceId = newSvc?.id;
        }

        if (targetServiceId) {
          const { data: existingSwa } = await supabase
            .from('service_worker_assignments')
            .select('id')
            .eq('service_id', targetServiceId)
            .eq('employee_id', employee.id)
            .is('end_date', null)
            .maybeSingle();

          if (!existingSwa) {
            await supabase.from('service_worker_assignments').insert({
              service_id: targetServiceId,
              employee_id: employee.id,
              start_date: startDate,
              end_date: endDate || null,
            });
          }
        }
      } catch (svcErr) {
        console.warn('Service sync note:', svcErr);
      }

      setResult({ url: res.shareableUrl, whatsappSent: res.whatsappSent, whatsappError: res.whatsappError });
      onAssigned();
    } catch (err: any) {
      toast.error(err.message ?? 'Assignment failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyUrl = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            {result ? 'Assignment Successful!' : 'Assign to Client'}
          </DialogTitle>
        </DialogHeader>

        {/* Success screen */}
        {result ? (
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-5 h-5 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-emerald-800">{employee.full_name} assigned!</p>
                {result.whatsappSent
                  ? <p className="text-xs text-emerald-600 mt-0.5">WhatsApp notification sent ✅</p>
                  : <p className="text-xs text-amber-600 mt-0.5">WhatsApp not sent — {result.whatsappError ?? 'no phone on file'}</p>}
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-500 font-medium mb-1.5 uppercase tracking-wider">Shareable ID Card Link</p>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-xs text-slate-700 flex-1 truncate font-mono">{result.url}</span>
                <button onClick={copyUrl} className="shrink-0 text-slate-400 hover:text-primary transition-colors">
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button onClick={onClose} className="w-full bg-primary hover:bg-primary/90 text-white">Done</Button>
          </div>
        ) : (
          /* Assignment form */
          <div className="space-y-4 mt-2">
            {/* Employee info card */}
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/10 rounded-xl">
              <Avatar className="w-11 h-11 ring-2 ring-primary/10">
                {employee.photo_url && <AvatarImage src={employee.photo_url} alt={employee.full_name} />}
                <AvatarFallback className="bg-primary text-white text-sm font-bold">
                  {getInitials(employee.full_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{employee.full_name}</p>
                <p className="text-xs text-primary font-medium">{employee.job_title}</p>
                <p className="font-mono text-[10px] text-slate-400 mt-0.5">{employee.employee_id}</p>
              </div>
            </div>

            {/* Client selector */}
            <div className="relative">
              <label className="text-sm font-medium text-slate-700">Select Client <span className="text-red-400">*</span></label>
              <div className="mt-1 relative">
                <Input
                  placeholder="Search for a client..."
                  value={selectedClient ? selectedClient.client_name : clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setSelectedClient(null); setClientDropdownOpen(true); }}
                  onFocus={() => setClientDropdownOpen(true)}
                  className="pr-8"
                />
                {selectedClient && (
                  <button
                    onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {clientDropdownOpen && !selectedClient && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {clients.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400">No clients found</div>
                  ) : (
                    clients.map(c => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-2.5 hover:bg-primary/5 text-sm text-slate-700 flex items-center gap-2 transition-colors"
                        onClick={() => { setSelectedClient(c); setClientDropdownOpen(false); }}
                      >
                        <div className="flex items-center justify-between gap-2 w-full">
                           <span className="font-bold flex-1 truncate">{c.client_name}</span>
                           <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-black uppercase tracking-tighter shrink-0">
                                {c.company_name}
                           </span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 w-full mt-0.5">
                          <span className="truncate">{c.phone_number || 'No contact'}</span>
                          <span className="flex items-center gap-1 group-hover:text-primary transition-colors">
                            Select <ExternalLink className="w-2.5 h-2.5" />
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                  <button
                    className="w-full text-left px-3 py-2.5 border-t border-slate-100 text-xs text-primary font-medium hover:bg-primary/5 flex items-center gap-2"
                    onClick={() => { setClientDropdownOpen(false); setShowNewClient(true); }}
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Add New Client
                  </button>
                </div>
              )}
            </div>

            {/* Inline new client form */}
            {showNewClient && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">New Client</p>
                <Input placeholder="Client Name *" value={newClientName} onChange={e => setNewClientName(e.target.value)} />
                <Input placeholder="WhatsApp Phone (optional)" value={newClientPhone} onChange={e => setNewClientPhone(e.target.value)} />
                <div className="flex gap-2">
                  <Button type="button" size="sm" className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    onClick={handleCreateClient} disabled={isCreatingClient}>
                    {isCreatingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add Client'}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewClient(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Assignment Date Range & Shift Type */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Assignment Period
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-600">Start Date <span className="text-red-400">*</span></label>
                    {lockedDateWarning && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5 text-amber-600" /> Min: {minStartDate}
                      </span>
                    )}
                  </div>
                  <Input 
                    type="date" 
                    className={`mt-1 text-sm ${lockedDateWarning ? 'border-amber-300 focus:ring-amber-500 bg-amber-50/20' : 'border-slate-200'}`}
                    value={startDate}
                    min={minStartDate || undefined}
                    onChange={e => {
                      if (minStartDate && e.target.value < minStartDate) {
                        toast.error(`Cannot assign before ${minStartDate} (worker was already released on prior duties)`);
                        return;
                      }
                      setStartDate(e.target.value);
                    }} 
                  />
                  {lockedDateWarning && (
                    <p className="text-[10px] text-amber-600 mt-1 font-medium leading-tight">
                      {lockedDateWarning}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-600">End Date (optional)</label>
                    <button
                      type="button"
                      onClick={() => {
                        if (isEndDateOpenEnded) {
                          setIsEndDateOpenEnded(false);
                          setEndDate(startDate || new Date().toISOString().slice(0, 10));
                        } else {
                          setIsEndDateOpenEnded(true);
                          setEndDate('');
                        }
                      }}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                        isEndDateOpenEnded 
                          ? 'bg-[#1AA6A8]/10 text-[#0E7C7E] border border-[#1AA6A8]/30 hover:bg-[#1AA6A8]/20' 
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {isEndDateOpenEnded ? '✓ Ongoing' : '+ Set Ongoing'}
                    </button>
                  </div>

                  {isEndDateOpenEnded ? (
                    <div 
                      onClick={() => {
                        setIsEndDateOpenEnded(false);
                        setEndDate(startDate || new Date().toISOString().slice(0, 10));
                      }}
                      className="w-full flex items-center justify-between h-9 rounded-md border border-dashed border-[#1AA6A8]/40 bg-[#1AA6A8]/5 px-3 py-1.5 text-xs text-[#0E7C7E] cursor-pointer hover:bg-[#1AA6A8]/10 transition-colors"
                      title="Click to set a specific end date"
                    >
                      <span className="font-semibold">Ongoing / Open-Ended</span>
                      <span className="text-[10px] text-slate-400">Click to set date</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        type="date"
                        className="text-sm border-slate-200 pr-7"
                        value={endDate}
                        min={startDate}
                        onChange={e => setEndDate(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsEndDateOpenEnded(true);
                          setEndDate('');
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-red-500 rounded"
                        title="Clear and make ongoing"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" /> Shift Requirement
                  </label>
                  <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                    Fixed by Client
                  </span>
                </div>
                <div className="w-full flex items-center justify-between h-10 rounded-lg border border-slate-200 bg-slate-50/90 px-3.5 py-2 text-sm font-semibold text-slate-800">
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${hoursPerDay === 24 ? 'bg-purple-500' : 'bg-primary'}`} />
                    {hoursPerDay === 24 ? '24-Hour Live-in Shift' : '10-Hour Day Shift'}
                  </span>
                  <span className="text-xs font-bold text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded shadow-2xs">
                    {hoursPerDay} hrs / day
                  </span>
                </div>
              </div>

              {/* Worker Payout Preview Badge */}
              <div className="bg-emerald-50/70 border border-emerald-200/70 rounded-lg p-2.5 flex items-center justify-between text-xs">
                <span className="font-semibold text-emerald-800 flex items-center gap-1.5">
                  <Coins className="w-3.5 h-3.5 text-emerald-600" /> Worker Duty Pay:
                </span>
                <span className="font-bold text-emerald-900">
                  ₹{hoursPerDay === 24 ? (employee.rate_24hr || 0) : (employee.rate_10hr || 0)} / day
                </span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
              <textarea
                rows={2}
                className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-transparent"
                placeholder="Any special instructions for this assignment..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
              <Button className="bg-primary hover:bg-primary/90 text-white gap-2" onClick={handleAssign}
                disabled={isSubmitting || !selectedClient || !startDate}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />}
                {isSubmitting ? 'Assigning...' : 'Assign & Send Link'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── ID Card Preview Dialog ────────────────────────────────

function IDCardPreviewDialog({ employee, open, onClose }: { employee: Employee | null; open: boolean; onClose: () => void }) {
  const [cardEmployee, setCardEmployee] = useState<Employee | null>(employee);

  useEffect(() => {
    if (!employee) {
      setCardEmployee(null);
      return;
    }
    setCardEmployee(employee);
    if (!open || !employee.id) return;
    supabase
      .from('employees')
      .select('*')
      .eq('id', employee.id)
      .single()
      .then(({ data }) => {
        if (data) setCardEmployee(data as Employee);
      });
  }, [open, employee]);

  if (!employee) return null;
  const emp = cardEmployee ?? employee;
  const dutyLabel = formatIdCardDuty(emp);
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Shield className="w-4 h-4 text-primary" /> ID Card Preview
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center py-4 gap-6">
          <div id="id-card-capture">
            <EmployeeIDCard
              employeeName={emp.full_name}
              employeeId={emp.employee_id}
              jobTitle={emp.job_title}
              photoUrl={emp.photo_url}
              aadhaarNumber={emp.aadhaar_number}
              address={emp.address}
              dob={emp.dob}
              duty={dutyLabel}
              experience={emp.experience}
              gender={emp.gender}
              variant="preview"
            />
          </div>
          
          <div className="w-full flex flex-col gap-2">
            <p className="text-[10px] text-slate-400 text-center px-4">This ID card is for internal identification. Click download to save a copy for the employee.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 text-xs h-9" onClick={onClose}>Close</Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-2" 
                onClick={() => window.print()}>
                <ExternalLink className="w-3.5 h-3.5" /> Print / Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Staff Details Dialog ──────────────────────────────────

function StaffDetailsDialog({ employee, open, onClose }: { employee: Employee | null; open: boolean; onClose: () => void }) {
  const { canViewPhoneNumbers } = useAuth();
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setLoading(true);
      getEmployeeDocuments(employee.id).then(d => {
        setDocs(d);
        setLoading(false);
      });
    }
  }, [open, employee]);

  if (!employee) return null;
  const paymentScheme = getPaymentScheme(employee);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-none shadow-2xl p-0 overflow-hidden">
        {/* Header Branding */}
        <div className="bg-gradient-to-r from-primary/95 to-brand-teal p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex gap-4 items-center">
              <Avatar className="w-20 h-20 border-4 border-white/20 shadow-xl">
                {employee.photo_url && <AvatarImage src={employee.photo_url} />}
                <AvatarFallback className="text-xl font-bold bg-white text-primary">
                  {getInitials(employee.full_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">{employee.full_name}</h2>
                <p className="opacity-90 font-medium">{employee.job_title}</p>
                <div className="flex gap-2 mt-2">
                  <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-mono tracking-wider uppercase border border-white/10">{employee.employee_id}</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-white/10">{employee.status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: Personal Data */}
            <div className="space-y-6">
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1.5 bg-primary/10 rounded-lg"><Users className="w-4 h-4 text-primary" /></div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Verification Profile</h4>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Aadhaar Identification</p>
                      <p className="text-sm font-semibold text-slate-800">{employee.aadhaar_number || 'Not Linked'}</p>
                    </div>
                    <Shield className="w-5 h-5 text-emerald-500/30" />
                  </div>
                  <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-300" />
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Primary Contact</p>
                      <p className="text-sm font-semibold text-slate-800">{employee.phone ? maskPhoneNumber(employee.phone, canViewPhoneNumbers) : '—'}</p>
                    </div>
                  </div>
                  <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-slate-300 mt-0.5" />
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Current Address</p>
                      <p className="text-sm font-semibold text-slate-800 leading-relaxed">{employee.address || 'Address not listed'}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1.5 bg-primary/10 rounded-lg"><Briefcase className="w-4 h-4 text-primary" /></div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Financial Meta</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                   <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                    <p className="text-[9px] text-slate-400 font-bold uppercase mb-1">
                      {paymentScheme.rateLabel}
                    </p>
                    <p className="text-sm font-bold text-slate-900">
                      ₹{paymentScheme.amount.toLocaleString('en-IN') || 0}
                    </p>
                  </div>
                  <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                    <p className="text-[9px] text-slate-400 font-bold uppercase mb-1">Payment Scheme</p>
                    <p className="text-sm border border-primary/20 bg-primary/10 px-2 py-0.5 rounded-full inline-block font-bold text-primary uppercase tracking-widest text-[10px]">
                      {paymentScheme.label}
                    </p>
                  </div>
                </div>
              </section>
            </div>

            {/* Right Column: Documents */}
            <div className="space-y-6">
              <section>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg"><FileText className="w-4 h-4 text-primary" /></div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Uploaded ID Proofs</h4>
                  </div>
                  {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                </div>
                
                <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {docs.length === 0 && !loading ? (
                    <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                      <FileText className="w-10 h-10 mx-auto text-slate-200 mb-3" />
                      <p className="text-xs text-slate-400">No documents verification on file</p>
                    </div>
                  ) : (
                    docs.map(doc => (
                      <a key={doc.id} href={doc.file_url} target="_blank" rel="noreferrer"
                        className="flex items-center justify-between p-3.5 bg-white border border-slate-100 rounded-xl hover:border-primary/40 hover:bg-primary/5 transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800 group-hover:text-primary transition-colors">{doc.file_name}</p>
                            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tight">{doc.file_type?.split('/')?.[1] || 'Verification Doc'}</p>
                          </div>
                        </div>
                        <div className="p-2 rounded-full group-hover:bg-primary/10 transition-colors">
                          <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-primary" />
                        </div>
                      </a>
                    ))
                  )}
                </div>
              </section>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
            <Button onClick={onClose} className="px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all shadow-lg shadow-slate-200">
              Close Record
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Available Workers Tab ─────────────────────────────────

function AvailableWorkersTab({ onAssign, onPreview, onViewDetails }: {
  onAssign: (emp: Employee) => void;
  onPreview: (emp: Employee) => void;
  onViewDetails: (emp: Employee) => void;
}) {
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [expandedWorkerIds, setExpandedWorkerIds] = useState<Record<string, boolean>>({});
  const debouncedSearch = useDebounce(search);

  const toggleWorkerExpanded = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedWorkerIds(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAvailableEmployees();
      setEmployees(data);
    } catch { toast.error('Failed to load available workers.'); }
    finally { setIsLoading(false); }
  }, []);

  const { user, canViewPhoneNumbers } = useAuth();
  const [requestDeleteEmp, setRequestDeleteEmp] = useState<Employee | null>(null);

  const handleDelete = async (emp: Employee) => {
    if (user?.role !== 'admin') {
      setRequestDeleteEmp(emp);
      return;
    }
    if (!confirm(`Are you sure you want to remove ${emp.full_name}? This will permanently delete their record.`)) return;
    try {
      await deleteEmployee(emp.id);
      toast.success(`${emp.full_name} removed from directory.`);
      setEmployees(prev => prev.filter(e => e.id !== emp.id));
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete employee.');
    }
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel('available-workers-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employees' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'worker_assignments' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  const filtered = employees.filter(e =>
    !debouncedSearch ||
    e.full_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    e.job_title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    e.employee_id.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  return (
    <>
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9 h-9 sm:h-10 text-xs sm:text-sm bg-white" placeholder="Search by name, role, or ID..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* View Mode Toggle: Table (default) / Grid */}
          <div className="flex items-center p-0.5 sm:p-1 bg-slate-100 rounded-xl border border-slate-200/60 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-white text-primary shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
              title="Table View (Row & Column)"
            >
              <LayoutList className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Table</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'grid' ? 'bg-white text-primary shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
              title="Grid View (Cards)"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>

          {viewMode === 'grid' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const allExpanded = filtered.length > 0 && filtered.every(e => expandedWorkerIds[e.id]);
                if (allExpanded) {
                  setExpandedWorkerIds({});
                } else {
                  const next: Record<string, boolean> = {};
                  filtered.forEach(e => { next[e.id] = true; });
                  setExpandedWorkerIds(next);
                }
              }}
              className="h-9 sm:h-10 px-2.5 sm:px-3 text-xs font-semibold gap-1.5 border-slate-200 text-slate-600 hover:text-primary hover:border-primary/30 shrink-0 cursor-pointer"
              title={filtered.length > 0 && filtered.every(e => expandedWorkerIds[e.id]) ? "Collapse details for all workers" : "Show details for all workers"}
            >
              {filtered.length > 0 && filtered.every(e => expandedWorkerIds[e.id]) ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5 text-primary" />
                  <span className="hidden sm:inline">Collapse All</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  <span className="hidden sm:inline">Expand All</span>
                </>
              )}
            </Button>
          )}

          <Button variant="outline" size="icon" onClick={load} title="Refresh" className="h-9 sm:h-10 w-9 sm:w-10 shrink-0 cursor-pointer">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {isLoading ? (
        viewMode === 'table' ? (
          <div className="bg-white border border-slate-100/80 rounded-[2rem] overflow-hidden shadow-sm shadow-slate-200/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/50 border-b border-slate-100">
                  <tr>
                    {['Sr No.', 'ID No.', 'Staff Member', 'Skills & Services', 'Contact & Joined', 'Status', 'Actions'].map(h => (
                      <th key={h} className={`px-5 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap ${h === 'Actions' ? 'text-right pr-6' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50/80">
                  {Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={i} cols={7} />)}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-3.5">
            {Array.from({ length: 12 }).map((_, i) => <WorkerCardSkeleton key={i} />)}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No available workers{search ? ' matching your search' : ''}</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white border border-slate-100/80 rounded-[2rem] overflow-hidden shadow-sm shadow-slate-200/50">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  {['Sr No.', 'ID No.', 'Staff Member', 'Skills & Services', 'Contact & Joined', 'Status', 'Actions'].map(h => (
                    <th key={h} className={`px-5 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap ${h === 'Actions' ? 'text-right pr-6' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50/80">
                {filtered.map((emp, idx) => (
                  <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors group/row">
                    <td className="px-5 py-4 whitespace-nowrap w-16">
                      <span className="font-mono font-bold text-xs text-slate-700">
                        {idx + 1}.
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="font-mono text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-1 rounded-md border border-slate-200">
                        {emp.employee_id || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div
                        className="flex items-center gap-3.5 cursor-pointer group/staff"
                        onClick={() => onViewDetails(emp)}
                        title="Click to view full profile"
                      >
                        <Avatar className="w-10 h-10 ring-2 ring-slate-100 group-hover/staff:ring-primary/20 transition-all duration-300 shrink-0">
                          {emp.photo_url && <AvatarImage src={emp.photo_url} alt={emp.full_name} className="object-cover" />}
                          <AvatarFallback className="bg-gradient-to-br from-primary to-[#063b3c] text-white text-xs font-bold">
                            {getInitials(emp.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-slate-800 group-hover/staff:text-primary transition-colors">{emp.full_name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">{emp.job_title || 'Staff'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 flex-wrap max-w-[220px]">
                        {emp.services && emp.services.length > 0 ? (
                          <>
                            {emp.services.slice(0, 2).map((s: string) => (
                              <span key={s} className="text-[10px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200/80 truncate max-w-[130px]">
                                {s}
                              </span>
                            ))}
                            {emp.services.length > 2 && (
                              <span
                                className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200"
                                title={emp.services.slice(2).join(', ')}
                              >
                                +{emp.services.length - 2}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-slate-400">General Care</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1 text-xs">
                        {emp.phone ? (
                          canViewPhoneNumbers ? (
                            <a
                              href={`tel:${emp.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-slate-600 hover:text-primary font-medium transition-colors flex items-center gap-1.5"
                            >
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {emp.phone}
                            </a>
                          ) : (
                            <span className="text-slate-600 font-medium flex items-center gap-1.5 font-mono">
                              <Phone className="w-3.5 h-3.5 text-slate-400" />
                              {maskPhoneNumber(emp.phone, false)}
                            </span>
                          )
                        ) : (
                          <span className="text-slate-400 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-slate-300" /> No Phone
                          </span>
                        )}
                        {emp.created_at && (
                          <span className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            Joined {new Date(emp.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/80 uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                        Available
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2 pr-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-3 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold gap-1.5 rounded-xl border border-emerald-100 cursor-pointer shadow-2xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreview(emp);
                          }}
                        >
                          <Shield className="w-3.5 h-3.5" /> ID Card
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 px-3.5 text-xs bg-primary hover:bg-primary/90 text-white font-bold gap-1.5 rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssign(emp);
                          }}
                        >
                          <Briefcase className="w-3.5 h-3.5" /> Assign
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                              title="More options"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => onViewDetails(emp)} className="gap-2 text-xs">
                              <Shield className="w-3.5 h-3.5" /> Full Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onPreview(emp)} className="gap-2 text-xs">
                              <FileText className="w-3.5 h-3.5" /> Preview ID Card
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditEmployee(emp)} className="gap-2 text-xs text-primary focus:text-primary">
                              <Edit2 className="w-3.5 h-3.5" /> Edit Employee
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(emp)} className="gap-2 text-xs text-red-500 focus:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" /> Delete Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
          {filtered.map((emp, idx) => {
            const isExpanded = !!expandedWorkerIds[emp.id];
            return (
              <div
                key={emp.id}
                className="group bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-5 shadow-2xs hover:shadow-md hover:border-primary/40 transition-all duration-200 flex flex-col justify-between relative overflow-hidden"
              >
                {/* Top Accent Gradient */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-teal-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                {/* 1. Card Top Bar: 1.) Serial Number on Top Left + Available Badge & Options on Right */}
                <div className="flex items-center justify-between gap-2 pb-3 border-b border-slate-100">
                  <span
                    className="font-mono font-bold text-slate-700 bg-slate-100 px-2.5 py-0.5 rounded-md text-xs border border-slate-200/90 shadow-2xs shrink-0"
                    title={`Worker #${idx + 1}`}
                  >
                    {idx + 1}.)
                  </span>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/80 uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                      Available
                    </span>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all cursor-pointer"
                          title="More options"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => onViewDetails(emp)} className="gap-2 text-xs">
                          <Shield className="w-3.5 h-3.5" /> Full Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onPreview(emp)} className="gap-2 text-xs">
                          <FileText className="w-3.5 h-3.5" /> Preview ID Card
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditEmployee(emp)} className="gap-2 text-xs text-primary focus:text-primary">
                          <Edit2 className="w-3.5 h-3.5" /> Edit Employee
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(emp)} className="gap-2 text-xs text-red-500 focus:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" /> Delete Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* 2. Worker Main Profile: Avatar + Full Name + Role & ID */}
                <div
                  className="flex items-center gap-3.5 pt-3.5 cursor-pointer group/profile"
                  onClick={() => onViewDetails(emp)}
                  title="Click to view full profile"
                >
                  <Avatar className="w-13 h-13 ring-2 ring-slate-100 group-hover/profile:ring-primary/30 group-hover/profile:shadow-md transition-all shrink-0">
                    {emp.photo_url && <AvatarImage src={emp.photo_url} alt={emp.full_name} className="object-cover" />}
                    <AvatarFallback className="bg-gradient-to-br from-primary to-[#063b3c] text-white font-bold text-sm">
                      {getInitials(emp.full_name)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-900 text-sm sm:text-base group-hover/profile:text-primary transition-colors leading-snug break-words">
                      {emp.full_name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {emp.employee_id && (
                        <span className="font-mono text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200">
                          {emp.employee_id}
                        </span>
                      )}
                      <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-100 truncate max-w-[180px]">
                        {emp.job_title || 'Caregiver'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3. Other Information: Skills & Contact Details */}
                <div className="mt-3.5 pt-3 border-t border-slate-100 flex flex-col gap-2 text-xs">
                  {/* Skills row */}
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 shrink-0">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" /> Skills:
                    </span>
                    <div className="flex items-center gap-1 flex-wrap min-w-0">
                      {emp.services && emp.services.length > 0 ? (
                        <>
                          {emp.services.slice(0, 2).map((s: string) => (
                            <span key={s} className="text-[10px] font-medium bg-slate-50 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200/80 truncate max-w-[110px]">
                              {s}
                            </span>
                          ))}
                          {emp.services.length > 2 && (
                            <span
                              className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200"
                              title={emp.services.slice(2).join(', ')}
                            >
                              +{emp.services.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400">General Care</span>
                      )}
                    </div>
                  </div>

                  {/* Contact & Joined row */}
                  <div className="flex items-center justify-between text-slate-500 text-[11px] pt-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      {emp.phone ? (
                        canViewPhoneNumbers ? (
                          <a
                            href={`tel:${emp.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-600 hover:text-primary font-medium transition-colors truncate"
                          >
                            {emp.phone}
                          </a>
                        ) : (
                          <span className="text-slate-600 font-medium truncate font-mono">
                            {maskPhoneNumber(emp.phone, false)}
                          </span>
                        )
                      ) : (
                        <span className="text-slate-400">No Phone</span>
                      )}
                    </div>

                    {emp.created_at && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        Joined {new Date(emp.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>

                {/* 4. Action Buttons */}
                <div className="flex items-center gap-2.5 pt-3.5 mt-3.5 border-t border-slate-100">
                  <Button
                    variant="outline"
                    className="flex-1 h-11 border-slate-200 text-slate-700 hover:border-primary/40 hover:bg-teal-50 hover:text-teal-800 transition-all rounded-xl text-xs sm:text-sm font-semibold gap-2 cursor-pointer shadow-2xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(emp);
                    }}
                  >
                    <Shield className="w-4 h-4 text-slate-400 group-hover:text-teal-600" /> ID Card
                  </Button>
                  <Button
                    className="flex-1 h-11 bg-primary hover:bg-primary/90 text-white gap-2 rounded-xl text-xs sm:text-sm font-bold shadow-sm shadow-primary/25 transition-all active:scale-95 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssign(emp);
                    }}
                  >
                    <Briefcase className="w-4 h-4 shrink-0" /> Assign
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    <AddEmployeeDialog
      employee={editEmployee}
      open={!!editEmployee}
      onClose={() => setEditEmployee(null)}
      onCreated={(updated: any) => {
        setEmployees(prev => prev.map(e => e.id === updated.id ? updated : e));
        setEditEmployee(null);
      }}
    />
    {requestDeleteEmp && (
      <RequestDeletionModal
        isOpen={Boolean(requestDeleteEmp)}
        onClose={() => setRequestDeleteEmp(null)}
        entityType="worker"
        entityId={requestDeleteEmp.id}
        entityName={requestDeleteEmp.full_name || 'Worker'}
        defaultActionType="move_to_trash"
      />
    )}
    </>
  );
}


// ── Active Assignments Tab ─────────────────────────────────

function ActiveAssignmentsTab({ onPreview }: { onPreview: (emp: Employee) => void }) {
  const [assignments, setAssignments] = useState<ActiveAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [resending, setResending] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('worker_assignments')
        .select(`
          *,
          employee:employees(*),
          client:clients(id, client_name, phone_number)
        `)
        .eq('assignment_status', 'active')
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      // Fetch id_card_links for each assignment and resolve client info
      const enriched = await Promise.all(
        (data ?? []).map(async (a: any) => {
          let clientData = a.client;
          if ((!clientData || !clientData.phone_number) && a.client_id) {
            const { data: lead } = await supabase
              .from('crm_leads')
              .select('id, full_name, phone_number')
              .eq('id', a.client_id)
              .maybeSingle();
            if (lead) {
              clientData = {
                id: lead.id,
                client_name: lead.full_name || clientData?.client_name || 'Client',
                phone_number: lead.phone_number || clientData?.phone_number || null,
              };
            }
          }

          const { data: link } = await supabase
            .from('id_card_links')
            .select('token, is_active')
            .eq('assignment_id', a.id)
            .eq('is_active', true)
            .maybeSingle();

          const token = link?.token ?? null;
          const shareableUrl = token ? buildShareableUrl(token) : null;
          return {
            ...a,
            employee: a.employee,
            client: clientData,
            shareableUrl,
            token
          };
        })
      );
      setAssignments(enriched);
    } catch (err: any) {
      console.error('Assignments load error:', err);
      toast.error('Failed to load active deployments.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('active-assignments-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'worker_assignments' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'employees' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  const handleResend = async (a: ActiveAssignment) => {
    setResending(a.id);
    try {
      let shareableUrl = a.shareableUrl;
      let token = a.token;

      // 1. If no active link exists yet, auto-create one on the fly!
      if (!shareableUrl || !token) {
        token = crypto.randomUUID().replace(/-/g, '');
        const d = new Date();
        d.setDate(d.getDate() + 60); // valid for 60 days
        const { error: linkErr } = await supabase
          .from('id_card_links')
          .insert({
            employee_id: a.employee.id,
            assignment_id: a.id,
            token,
            is_active: true,
            expires_at: d.toISOString(),
          });

        if (linkErr) throw new Error(`Could not create ID link: ${linkErr.message}`);
        shareableUrl = buildShareableUrl(token);

        // Update local state so auth link shows immediately in table
        setAssignments(prev => prev.map(item => item.id === a.id ? { ...item, token, shareableUrl } : item));
      }

      // 2. Resolve client phone number reliably
      let phone = a.client?.phone_number;
      if (!phone && a.client_id) {
        const [crmRes, clientRes] = await Promise.all([
          supabase.from('crm_leads').select('phone_number').eq('id', a.client_id).maybeSingle(),
          supabase.from('clients').select('phone_number').eq('id', a.client_id).maybeSingle(),
        ]);
        phone = crmRes.data?.phone_number || clientRes.data?.phone_number;
      }

      if (!phone) {
        toast.error('No phone number found for this client to send WhatsApp.');
        return;
      }

      // 3. Send WhatsApp
      const err = await sendIDCardLinkToClient(
        phone,
        a.employee.full_name,
        a.employee.job_title || 'Care Attendant',
        shareableUrl
      );
      if (err) {
        toast.error(`WhatsApp dispatch failed: ${err}`);
      } else {
        toast.success(`ID Card link sent to ${a.client?.client_name || 'client'} on WhatsApp! 📱`);
      }
    } catch (err: any) {
      console.error('Error resending ID card link:', err);
      toast.error(err.message || 'Failed to send ID card link.');
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-sm font-bold text-slate-800 tracking-tight">
            {assignments.length} ACTIVE DEPLOYMENT{assignments.length !== 1 ? 'S' : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2 h-9 rounded-xl border-slate-200 text-slate-600">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-100/80 rounded-[2rem] overflow-hidden shadow-sm shadow-slate-200/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                {['Sr No.', 'ID No.', 'Staff Member', 'Client Name', 'Deployment Date', 'Auth Link', 'Deployment Actions'].map(h => (
                  <th key={h} className="px-5 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50/80">
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} cols={7} />)
                : assignments.length === 0
                  ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-20 text-center text-slate-400">
                        <div className="w-16 h-16 rounded-full bg-slate-50 mx-auto mb-4 flex items-center justify-center opacity-40">
                          <Briefcase className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="font-bold text-slate-300 uppercase tracking-widest text-xs">No active deployments found</p>
                      </td>
                    </tr>
                  )
                  : assignments.map((a, idx) => (
                    <tr key={a.id} className="hover:bg-slate-50/50 transition-colors group/row">
                      <td className="px-5 py-4 whitespace-nowrap w-16">
                        <span className="font-mono font-bold text-xs text-slate-700">
                          {idx + 1}.
                        </span>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="font-mono text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-1 rounded-md border border-slate-200">
                          {a.employee.employee_id}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3.5">
                          <Avatar className="w-10 h-10 ring-2 ring-slate-100 group-hover/row:ring-primary/20 transition-all duration-300">
                            {a.employee.photo_url && <AvatarImage src={a.employee.photo_url} alt={a.employee.full_name} className="object-cover" />}
                            <AvatarFallback className="bg-primary text-white text-xs font-bold">
                              {getInitials(a.employee.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-slate-800 group-hover/row:text-primary transition-colors">{a.employee.full_name}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">{a.employee.job_title}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col">
                          <p className="font-bold text-slate-700">{a.client?.client_name ?? '—'}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="w-1 h-1 rounded-full bg-emerald-400" />
                            <span className="text-[10px] font-bold text-emerald-600 uppercase">Confirmed</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs font-medium text-slate-500">
                        {new Date(a.assigned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-5 py-4">
                        {a.shareableUrl ? (
                          <div className="flex items-center gap-1.5 bg-slate-50 hover:bg-white p-1 rounded-lg border border-transparent hover:border-slate-200 transition-all w-fit">
                            <span className="font-mono text-[10px] text-slate-500 max-w-[80px] truncate ml-1">...{a.token?.slice(-8)}</span>
                            <div className="flex items-center gap-0.5 ml-1 pr-1 border-l border-slate-200 pl-1">
                               <CopyButton text={a.shareableUrl} />
                               <a href={a.shareableUrl} target="_blank" rel="noreferrer"
                                className="p-1 rounded text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors">
                                <ExternalLink className="w-3 h-3" />
                               </a>
                            </div>
                          </div>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" className="h-8 px-3 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold gap-1.5 rounded-xl border border-emerald-100 cursor-pointer"
                            onClick={() => onPreview(a.employee)}>
                            <Shield className="w-3 h-3" /> ID Card
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-3 text-xs bg-primary/5 text-primary hover:bg-primary/10 font-bold gap-1.5 rounded-xl border border-primary/10 cursor-pointer"
                            onClick={() => handleResend(a)} disabled={resending === a.id}>
                            {resending === a.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <MessageCircle className="w-3 h-3" />}
                            Resend Link
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── All Employees Tab ─────────────────────────────────────

function AllEmployeesTab({ onPreview, onViewDetails, refreshTrigger }: { 
  onPreview: (emp: Employee) => void; 
  onViewDetails: (emp: Employee) => void;
  refreshTrigger: number; 
}) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | 'all'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmStatusChange, setConfirmStatusChange] = useState<{ emp: Employee, newStatus: EmployeeStatus } | null>(null);
  const debouncedSearch = useDebounce(search);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .is('deleted_at', null)
        .order('full_name', { ascending: true });
      if (error) throw error;
      setEmployees(data ?? []);
    } catch { toast.error('Failed to load employees.'); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const handleStatusChange = async (emp: Employee, newStatus: EmployeeStatus) => {
    if (emp.status === 'assigned' && newStatus === 'available') {
      setConfirmStatusChange({ emp, newStatus });
      return;
    }

    setUpdatingId(emp.id);
    try {
      const updated = await updateEmployeeStatus(emp.id, newStatus);
      setEmployees(prev => prev.map(e => e.id === updated.id ? updated : e));
      toast.success(`${emp.full_name} → ${newStatus}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally { setUpdatingId(null); }
  };

  const executeStatusChange = async () => {
    if (!confirmStatusChange) return;
    const { emp, newStatus } = confirmStatusChange;
    
    setUpdatingId(emp.id);
    const targetStatus = newStatus;
    setConfirmStatusChange(null);
    
    try {
      // Find active assignment
      const { data: assignment } = await supabase
        .from('worker_assignments')
        .select('id')
        .eq('employee_id', emp.id)
        .eq('assignment_status', 'active')
        .maybeSingle();

      if (assignment) {
        await deactivateIDCardLink(assignment.id, 'cancelled');
        // deactivateIDCardLink updates the employee status to available automatically
      } else {
        await updateEmployeeStatus(emp.id, targetStatus);
      }
      
      // Refresh list
      load();
      toast.success(`${emp.full_name} is now ${targetStatus} and released from client.`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = employees.filter(e => {
    const q = debouncedSearch.toLowerCase();
    const matchSearch = !q || e.full_name.toLowerCase().includes(q) || e.employee_id.toLowerCase().includes(q) || e.job_title.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || e.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-80 group">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
          <Input 
            className="pl-10 h-11 bg-white border-slate-200 rounded-xl focus:ring-primary/20 focus:border-primary/30 transition-all shadow-sm" 
            placeholder="Search name, role, or ID..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 no-scrollbar">
          <div className="flex p-1 bg-slate-100/80 backdrop-blur-md rounded-xl border border-slate-200/50">
            {(['all', 'available', 'assigned', 'inactive'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-1.5 text-[11px] font-black uppercase tracking-wider rounded-lg transition-all duration-300 whitespace-nowrap
                  ${statusFilter === s 
                    ? 'bg-white text-primary shadow-sm shadow-slate-200 translate-z-0 scale-[1.02]' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/40'}`}
              >
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
          <Button 
             variant="outline" 
             size="icon" 
             onClick={load} 
             className="h-9 w-9 border-slate-200 rounded-xl bg-white hover:bg-slate-50 transition-colors shadow-sm shrink-0"
             title="Refresh Directory"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-100/80 rounded-[2rem] overflow-hidden shadow-sm shadow-slate-200/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                {['Sr No.', 'Worker info', 'Services & Payment', 'Status', 'Quick Actions'].map(h => (
                   <th key={h} className={`px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap ${h === 'Quick Actions' ? 'text-right' : ''}`}>
                    {h}
                   </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50/80">
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={5} />)
                : filtered.length === 0
                  ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-24 text-center text-slate-400">
                        <div className="w-16 h-16 rounded-full bg-slate-50 mx-auto mb-4 flex items-center justify-center opacity-40">
                          <Users className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="font-bold text-slate-300 uppercase tracking-widest text-xs">No employees found in directory</p>
                      </td>
                    </tr>
                  )
                  : filtered.map((emp, idx) => (
                    <tr key={emp.id} className="hover:bg-slate-50/50 transition-all group/row cursor-pointer" onClick={() => onViewDetails(emp)}>
                      <td className="px-6 py-5 whitespace-nowrap w-16">
                        <span className="font-mono font-bold text-xs text-slate-700">
                          {idx + 1}.
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar className="w-11 h-11 ring-2 ring-slate-100 group-hover/row:ring-primary/20 transition-all duration-300 shadow-sm">
                              {emp.photo_url && <AvatarImage src={emp.photo_url} alt={emp.full_name} className="object-cover" />}
                              <AvatarFallback className="bg-gradient-to-br from-primary/80 to-primary text-white text-xs font-bold">
                                {getInitials(emp.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm transition-transform duration-300 group-hover/row:scale-110
                              ${emp.status === 'available' ? 'bg-emerald-400' : emp.status === 'assigned' ? 'bg-blue-400' : 'bg-slate-300'}`} 
                            />
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 group-hover/row:text-primary transition-colors text-base tracking-tight">{emp.full_name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 font-mono">
                                {emp.employee_id}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{emp.job_title}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap max-w-[180px]">
                            {emp.services && emp.services.length > 0 ? (
                              emp.services.map(s => (
                                <span key={s} className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded shadow-sm border border-slate-200">
                                  {s}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">No services listed</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[10px] font-black text-primary uppercase tracking-widest bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                              {getPaymentScheme(emp).label}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {statusBadge(emp.status)}
                      </td>
                      <td className="px-6 py-5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                           <Button size="sm" variant="ghost" className="h-8 px-3 text-[10px] font-black uppercase tracking-wider bg-slate-50 text-slate-600 hover:bg-primary/5 hover:text-primary rounded-xl transition-all border border-slate-100"
                            onClick={() => onPreview(emp)}>
                            <ExternalLink className="w-3 h-3 mr-1.5" /> ID Card
                           </Button>
                           <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-100 transition-colors"
                                disabled={updatingId === emp.id}>
                                {updatingId === emp.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 p-1.5 rounded-2xl shadow-xl border-slate-100/60 backdrop-blur-lg bg-white/95">
                               <p className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">Update status</p>
                              {(['available', 'assigned', 'inactive'] as EmployeeStatus[]).map(s => (
                                <DropdownMenuItem key={s} onClick={() => handleStatusChange(emp, s)}
                                  className={`rounded-xl px-3 py-2 mb-0.5 last:mb-0 transition-colors cursor-pointer
                                    ${emp.status === s ? 'bg-primary/5 text-primary font-bold' : 'text-slate-600 hover:bg-slate-50'}`}>
                                  {s.charAt(0).toUpperCase() + s.slice(1)}
                                  {emp.status === s && <Check className="w-3.5 h-3.5 ml-auto" />}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                           </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Directory Summary
           </p>
           <p className="text-xs font-bold text-slate-500 bg-white px-3 py-1 rounded-full border border-slate-100 shadow-sm">
             {filtered.length} Staff Members <span className="text-slate-300 mx-1">|</span> {statusFilter === 'all' ? 'All Records' : statusFilter.toUpperCase()}
           </p>
        </div>
      </div>

      {/* Confirmation Dialog for Status Change */}
      <Dialog open={!!confirmStatusChange} onOpenChange={(open) => !open && setConfirmStatusChange(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Status Change
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              You are moving <span className="font-bold text-slate-900">{confirmStatusChange?.emp.full_name}</span> from <span className="font-bold text-blue-600">Assigned</span> to <span className="font-bold text-emerald-600">Available</span>.
            </p>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl">
              <p className="text-xs text-amber-700 font-medium">
                This will automatically release the worker from their currently assigned client and deactivate their active ID card link. Are you sure you want to proceed?
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmStatusChange(null)}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={executeStatusChange}
            >
              Yes, Make Available
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Recycle Bin Tab ───────────────────────────────────────

function RecycleBinTab({ refreshTrigger }: { refreshTrigger: number }) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const { user } = useAuth();
  const [requestDeleteEmp, setRequestDeleteEmp] = useState<Employee | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getDeletedEmployees();
      setEmployees(data);
    } catch { toast.error('Failed to load recycle bin.'); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  const handleRestore = async (emp: Employee) => {
    setActingId(emp.id);
    try {
      await restoreEmployee(emp.id);
      toast.success(`${emp.full_name} restored to directory.`);
      setEmployees(prev => prev.filter(e => e.id !== emp.id));
    } catch (err: any) {
      toast.error(err.message);
    } finally { setActingId(null); }
  };

  const handlePermanentDelete = async (emp: Employee) => {
    if (user?.role !== 'admin') {
      setRequestDeleteEmp(emp);
      return;
    }
    if (!confirm(`WARNING: Are you sure you want to PERMANENTLY delete ${emp.full_name}? This cannot be undone.`)) return;
    setActingId(emp.id);
    try {
      await permanentlyDeleteEmployee(emp.id);
      toast.success(`${emp.full_name} permanently removed.`);
      setEmployees(prev => prev.filter(e => e.id !== emp.id));
    } catch (err: any) {
      toast.error(err.message);
    } finally { setActingId(null); }
  };

  const handleWipeAll = async () => {
    if (employees.length === 0) return;
    if (user?.role !== 'admin') {
      toast.error('Only System Admin can wipe recycle bin.');
      return;
    }
    if (!confirm(`CRITICAL WARNING: Are you sure you want to PERMANENTLY delete ALL ${employees.length} records in the recycle bin? This action is IRREVERSIBLE.`)) return;
    
    setIsLoading(true);
    try {
      await permanentlyDeleteAllDeletedEmployees();
      toast.success(`Successfully cleared ${employees.length} records from recycle bin.`);
      setEmployees([]);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
           <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
            <Trash2 className="w-4 h-4 text-red-500" />
           </div>
           <p className="text-sm font-bold text-slate-800 tracking-tight uppercase">
              RECYCLE BIN <span className="text-slate-400 ml-1">({employees.length})</span>
           </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleWipeAll} 
            disabled={isLoading || employees.length === 0}
            className="gap-2 h-9 rounded-xl bg-red-600 hover:bg-red-700 transition-all shadow-md shadow-red-500/20 font-bold disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
          >
            <Trash2 className="w-3.5 h-3.5" /> Wipe All Trash
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="gap-2 h-9 rounded-xl border-slate-200 text-slate-600 bg-white hover:bg-slate-50 transition-colors shadow-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} /> Refresh Bin
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-100/80 rounded-[2rem] overflow-hidden shadow-sm shadow-slate-200/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                {['Sr No.', 'Staff Member', 'Job Title', 'Last Status', 'Deleted On', 'Actions'].map(h => (
                   <th key={h} className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50/80">
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} cols={6} />)
                : employees.length === 0
                  ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-20 text-center text-slate-400">
                        <div className="w-16 h-16 rounded-full bg-slate-50 mx-auto mb-4 flex items-center justify-center opacity-40">
                          <Trash2 className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="font-bold text-slate-300 uppercase tracking-widest text-xs">No records in recycle bin</p>
                      </td>
                    </tr>
                  )
                  : employees.map((emp, idx) => (
                    <tr key={emp.id} className="hover:bg-slate-50/50 transition-all group/row">
                      <td className="px-6 py-4 whitespace-nowrap w-16">
                        <span className="font-mono font-bold text-xs text-slate-700">
                          {idx + 1}.
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3.5">
                          <Avatar className="w-10 h-10 ring-2 ring-slate-100 transition-all duration-300">
                            {emp.photo_url && <AvatarImage src={emp.photo_url} alt={emp.full_name} className="object-cover" />}
                            <AvatarFallback className="bg-slate-200 text-slate-500 text-xs font-bold">
                              {getInitials(emp.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-slate-700">{emp.full_name}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">{emp.employee_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 font-medium">{emp.job_title}</td>
                      <td className="px-6 py-4 opacity-70 scale-90 origin-left">{statusBadge(emp.status)}</td>
                      <td className="px-6 py-4 text-slate-400 text-xs font-medium italic">
                        {(emp as any).deleted_at ? new Date((emp as any).deleted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Recently'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 px-3 text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl transition-all border border-emerald-100"
                            onClick={() => handleRestore(emp)}
                            disabled={actingId === emp.id}
                          >
                            <RotateCcw className="w-3 h-3 mr-1.5" /> Restore
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 px-3 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                            onClick={() => handlePermanentDelete(emp)}
                            disabled={actingId === emp.id}
                          >
                            <Trash2 className="w-3 h-3 mr-1.5" /> Wipe
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      </div>
      {requestDeleteEmp && (
        <RequestDeletionModal
          isOpen={Boolean(requestDeleteEmp)}
          onClose={() => setRequestDeleteEmp(null)}
          entityType="worker"
          entityId={requestDeleteEmp.id}
          entityName={requestDeleteEmp.full_name || 'Worker'}
          defaultActionType="permanent_delete"
        />
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

interface WorkerAllocationProps {
  isEmbedded?: boolean;
}

export default function WorkerAllocation({ isEmbedded = false }: WorkerAllocationProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabParam = searchParams.get('subtab');
  const [activeSubTab, setActiveSubTab] = useState<'available' | 'assignments' | 'all' | 'deleted'>(
    subtabParam && ['available', 'assignments', 'all', 'deleted'].includes(subtabParam)
      ? (subtabParam as any)
      : 'available'
  );

  useEffect(() => {
    const param = searchParams.get('subtab') as any;
    if (param && ['available', 'assignments', 'all', 'deleted'].includes(param)) {
      setActiveSubTab(param);
    } else if (!param && activeSubTab !== 'available') {
      setActiveSubTab('available');
    }
  }, [searchParams]);

  const handleSubTabChange = (val: string) => {
    setActiveSubTab(val as any);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'available') {
        next.delete('subtab');
      } else {
        next.set('subtab', val);
      }
      return next;
    });
  };

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [assignEmployee, setAssignEmployee] = useState<Employee | null>(null);
  const [previewEmployee, setPreviewEmployee] = useState<Employee | null>(null);
  const [detailsEmployee, setDetailsEmployee] = useState<Employee | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  // Auto-seed Alexander Mitchell if missing
  useEffect(() => {
    const seedAlexander = async () => {
      try {
        const { data: existing } = await supabase.from('employees').select('id').eq('full_name', 'Alexander Mitchell').single();
        if (!existing) {
          // Add Alexander if he doesn't exist
          await supabase.from('employees').insert({
            full_name: 'Alexander Mitchell',
            job_title: 'Senior Registered Nurse',
            department: 'ICU / Critical Care',
            status: 'available',
            photo_url: 'https://images.unsplash.com/photo-1576091160550-217359f42f8c?q=80&w=2070&auto=format&fit=crop',
            phone: '919876543210',
            address: 'Navi Mumbai, MH',
            hourly_rate: 0,
            monthly_daily_rate: 1500,
            short_term_daily_rate: 1800,
            deposit_received: 15000,
            employee_id: 'EMP-001234'
          });
          setRefreshTrigger(t => t + 1);
        }
      } catch (e) {
        console.warn('Auto-seed check failed:', e);
      }
    };
    seedAlexander();
  }, []);

  const handleEmployeeCreated = (emp: Employee, autoPreview = false) => {
    setRefreshTrigger(t => t + 1);
    toast.success(`${emp.full_name} added to directory!`);
    if (autoPreview) {
      setPreviewEmployee(emp);
    }
  };

  return (
    <div className={`space-y-6 ${isEmbedded ? '' : 'p-6'}`}>
      {/* Header - only show if not embedded */}
      {!isEmbedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Worker Allocation</h1>
            <p className="text-sm text-slate-500 mt-1">Assign healthcare workers to clients and manage digital ID cards</p>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-md shadow-primary/20 gap-2 shrink-0"
          >
            <UserPlus className="w-4 h-4" />
            Add New Employee
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeSubTab} onValueChange={handleSubTabChange} className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="w-full sm:w-auto overflow-x-auto hide-scrollbar">
            <TabsList className="w-full sm:w-fit min-w-full sm:min-w-0 bg-slate-100/80 backdrop-blur-md border border-slate-200/50 p-1 sm:p-1.5 rounded-xl sm:rounded-2xl shadow-sm flex items-center justify-between sm:justify-start gap-1 sm:gap-0">
              <TabsTrigger value="available" className="flex-1 sm:flex-initial gap-1.5 sm:gap-2.5 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg sm:rounded-xl px-2 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-bold sm:font-black uppercase tracking-tight sm:tracking-wider transition-all duration-300 whitespace-nowrap shrink-0">
                <Users className="w-3.5 h-3.5 shrink-0" />
                <span>Available<span className="hidden sm:inline"> Workers</span></span>
              </TabsTrigger>
              <TabsTrigger value="assignments" className="flex-1 sm:flex-initial gap-1.5 sm:gap-2.5 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg sm:rounded-xl px-2 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-bold sm:font-black uppercase tracking-tight sm:tracking-wider transition-all duration-300 whitespace-nowrap shrink-0">
                <Briefcase className="w-3.5 h-3.5 shrink-0" />
                <span>Deployments</span>
              </TabsTrigger>
              <TabsTrigger value="all" className="flex-1 sm:flex-initial gap-1.5 sm:gap-2.5 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm rounded-lg sm:rounded-xl px-2 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-bold sm:font-black uppercase tracking-tight sm:tracking-wider transition-all duration-300 whitespace-nowrap shrink-0">
                <Shield className="w-3.5 h-3.5 shrink-0" />
                <span>Directory</span>
              </TabsTrigger>
              <TabsTrigger value="deleted" className="flex-1 sm:flex-initial gap-1.5 sm:gap-2.5 data-[state=active]:bg-white data-[state=active]:text-red-500 data-[state=active]:shadow-sm rounded-lg sm:rounded-xl px-2 sm:px-5 py-1.5 sm:py-2 text-[10px] sm:text-[11px] font-bold sm:font-black uppercase tracking-tight sm:tracking-wider transition-all duration-300 whitespace-nowrap shrink-0">
                <Trash2 className="w-3.5 h-3.5 shrink-0" />
                <span>Trash</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {isEmbedded && (
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white shadow-sm gap-2 shrink-0"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Add Employee
            </Button>
          )}
        </div>

        <TabsContent value="available">
          <AvailableWorkersTab
            onAssign={emp => setAssignEmployee(emp)}
            onPreview={emp => setPreviewEmployee(emp)}
            onViewDetails={emp => setDetailsEmployee(emp)}
          />
        </TabsContent>

        <TabsContent value="assignments">
          <ActiveAssignmentsTab onPreview={emp => setPreviewEmployee(emp)} />
        </TabsContent>

        <TabsContent value="all">
          <AllEmployeesTab 
            onPreview={emp => setPreviewEmployee(emp)} 
            onViewDetails={emp => setDetailsEmployee(emp)}
            refreshTrigger={refreshTrigger} 
          />
        </TabsContent>


        <TabsContent value="deleted">
          <RecycleBinTab refreshTrigger={refreshTrigger} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddEmployeeDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onCreated={handleEmployeeCreated}
      />
      <AssignDialog
        employee={assignEmployee}
        open={!!assignEmployee}
        onClose={() => setAssignEmployee(null)}
        onAssigned={() => setRefreshTrigger(t => t + 1)}
      />
      <IDCardPreviewDialog
        employee={previewEmployee}
        open={!!previewEmployee}
        onClose={() => setPreviewEmployee(null)}
      />
      <StaffDetailsDialog
        employee={detailsEmployee}
        open={!!detailsEmployee}
        onClose={() => setDetailsEmployee(null)}
      />
    </div>
  );
}
